import { MutableRefObject, useEffect, useState, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import MarkdownPlugin from './MarkdownPlugin';
import { ListNode, ListItemNode } from '@lexical/list';
import { HeadingNode } from '@lexical/rich-text';
import { LineBreakNode } from 'lexical';
import styles from './LightEditor.module.scss';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EditorState } from 'lexical';
import { $convertToMarkdownString } from '@lexical/markdown';
import React from 'react';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';

interface LightEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  onEditorBlur: () => void;
  minHeight?: number;
  isEditable: boolean;
  autoFocus?: boolean;
}

function onError(error: Error): void {
  console.error(error);
}

const theme = {};

export default function LightEditor({
  content,
  onContentChange,
  onEditorBlur,
  minHeight,
  isEditable,
  autoFocus = false,
}: Readonly<LightEditorProps>) {
  const [currentContent, setCurrentContent] = useState(content);
  const componentRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const initialConfig = {
    namespace: 'InlineEditor',
    theme,
    onError,
    editable: true,
    nodes: [HeadingNode, ListItemNode, ListNode, LineBreakNode], // Register the HeadingNode here
  };

  useEffect(() => {
    setCurrentContent(content);
  }, [content]);

  const handleEditorChange = (editorState: EditorState) => {
    const markdownContent = editorState.read(() => $convertToMarkdownString());
    onContentChange(markdownContent);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (componentRef.current && !componentRef.current.contains(event.relatedTarget)) {
      onEditorBlur();
    }
  };

  return (
    <div
      ref={componentRef}
      onBlur={handleBlur}
      className={styles.light_editor}
    >
      <LexicalComposer
        initialConfig={{
          ...initialConfig,
        }}
      >
        <MarkdownPlugin
          isEditable={isEditable}
          content={currentContent}
        />
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={`editor-input ${!isEditable ? styles.editor__disabled : ''}`}
              style={{
                padding: '10px',
                margin: '10px 0',
                minHeight: minHeight ? `${minHeight}px` : '30px',
              }}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <OnChangePlugin onChange={handleEditorChange} />
        {autoFocus && <AutoFocusPlugin />}
      </LexicalComposer>
    </div>
  );
}
